/**
 * Loading the maps Conductor draws on (#256, #248, #250): Google Maps (loaded globally by index.html), and the vendored
 * fallbacks for where Conductor's key isn't allowed (local worktree ports): MapLibre with CARTO's keyless tiles. Plus
 * deck.gl for the suburb map's polygons. Each vendored bundle is loaded once, when a map first opens.
 */

declare const google: any;

const DECK_SRC = 'assets/vendor/deck.gl-9.0.36/dist.min.js';
const MAPLIBRE_SRC = 'assets/vendor/maplibre-gl-4.7.1/maplibre-gl.js';
const MAPLIBRE_CSS = 'assets/vendor/maplibre-gl-4.7.1/maplibre-gl.css';
export const CARTO_STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

/**
 * Shown wherever a map fell back to the open basemap because Google refused Conductor's key at this web address. The
 * key (Google Cloud project conductor-354607) allows fdconductor.com, its subdomains and the local ports; any other
 * address has to be added to it.
 */
export const GOOGLE_REFUSED_NOTE =
  "Google Maps refused this web address, so this map uses an open basemap (everything else works the same). Add the address to Conductor's Maps key in Google Cloud (project conductor-354607).";
const umdPromises = new Map<string, Promise<any>>();

/** Load a vendored UMD bundle once and return the global it defines. */
function loadUmd(src: string, globalName: string): Promise<any> {
  const w = window as any;
  if (w[globalName]) return Promise.resolve(w[globalName]);
  let p = umdPromises.get(src);
  if (!p) {
    p = new Promise((resolve, reject) => {
      // A UMD registers with an AMD loader when one is present (Monaco's, once any code editor has opened) and then
      // never sets its global. Hide `define` while it runs.
      const amd = w.define;
      w.define = undefined;
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => {
        w.define = amd;
        if (w[globalName]) resolve(w[globalName]);
        else reject(new Error(`${src} loaded but did not define ${globalName}`));
      };
      s.onerror = () => {
        w.define = amd;
        umdPromises.delete(src);
        reject(new Error(`could not load ${src}`));
      };
      document.head.appendChild(s);
    });
    umdPromises.set(src, p);
  }
  return p;
}

export const loadDeck = () => loadUmd(DECK_SRC, 'deck');

export function loadMapLibre(): Promise<any> {
  if (!document.querySelector(`link[href="${MAPLIBRE_CSS}"]`)) {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = MAPLIBRE_CSS;
    document.head.appendChild(css);
  }
  return loadUmd(MAPLIBRE_SRC, 'maplibregl');
}

/**
 * Google reports a key it won't serve for this address (RefererNotAllowedMapError) by calling window.gm_authFailure,
 * not by failing the script load. index.html records an early failure in __gmAuthFailed; this resolves on a later one.
 */
export function googleAuthFailure(): Promise<void> {
  const w = window as any;
  if (w.__gmAuthFailed) return Promise.resolve();
  return new Promise((resolve) => {
    const prev = w.gm_authFailure;
    w.gm_authFailure = () => {
      w.__gmAuthFailed = true;
      if (typeof prev === 'function') prev();
      resolve();
    };
  });
}

/** Google Maps is loaded globally by index.html (async); wait for it rather than loading a second copy. */
export function waitForGoogleMaps(timeoutMs = 15000): Promise<void> {
  const ready = () => typeof google !== 'undefined' && !!google?.maps?.Map;
  if (ready()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const t = setInterval(() => {
      if (ready()) { clearInterval(t); resolve(); }
      else if (Date.now() - started > timeoutMs) { clearInterval(t); reject(new Error('Google Maps did not load')); }
    }, 100);
  });
}
